import { Modal, ModalProps } from '@components/modal';
import { Box } from '@mui/material';
import { useAtomValue } from 'jotai';
import {
  ComponentProps,
  JSXElementConstructor,
  PropsWithChildren,
  Suspense,
  useCallback,
  useRef,
} from 'react';
import {
  getCloseInterceptor,
  hideModal as hideModalAction,
  modalsAtom,
  showModal as showModalAction,
} from './modalAtoms';

const generateModalKey = () => `modal-${Date.now()}-${Math.random().toString(36).slice(2, 11)}`;

export type UseModalProps<T extends JSXElementConstructor<Record<string, unknown>>> = {
  component?: T;
  // `onClose` and `modalKey` are injected by ModalContainer, so callers shouldn't pass them.
  componentProps?: Omit<ComponentProps<T>, 'onClose' | 'modalKey'> &
    Partial<Pick<ComponentProps<T>, 'onClose'>>;
  modalProps?: Partial<ModalProps>;
  onModalOpen?: () => void | Promise<void>;
};

export const ModalProvider = ({ children }: PropsWithChildren) => {
  if (typeof window === 'undefined') {
    return <div />;
  }
  return (
    <>
      {children}
      <ModalContainer />
    </>
  );
};

export const useModal = <T extends JSXElementConstructor<never>>({
  component,
  componentProps,
  modalProps,
  onModalOpen,
}: UseModalProps<T>) => {
  const keyRef = useRef<string | null>(null);
  // Capture inputs in a ref so showModal/hideModal can be `useCallback([])` and stay
  // stable across renders. Most callers pass fresh object literals for componentProps
  // and modalProps; without this, showModal would get a new identity every render and
  // any consumer that depends on it (e.g. in a useEffect dep array) would loop.
  const propsRef = useRef({ component, componentProps, modalProps, onModalOpen });
  propsRef.current = { component, componentProps, modalProps, onModalOpen };

  const showModal = useCallback(
    (modalData?: {
      componentProps?: Record<string, unknown> & { onClose?: () => void };
      modalProps?: Partial<ModalProps>;
    }) => {
      const key = generateModalKey();
      keyRef.current = key;
      const {
        component: c,
        componentProps: cp,
        modalProps: mp,
        onModalOpen: omo,
      } = propsRef.current;
      void showModalAction(
        c as Parameters<typeof showModalAction>[0],
        key,
        omo,
        { ...cp, ...modalData?.componentProps } as Parameters<typeof showModalAction>[3],
        { ...mp, ...modalData?.modalProps },
      );
    },
    [],
  );

  const hideModal = useCallback(() => {
    if (keyRef.current) {
      hideModalAction(keyRef.current);
    }
  }, []);

  return { showModal, hideModal };
};

const ModalContainer = () => {
  const configs = useAtomValue(modalsAtom);
  const openModals = Object.keys(configs ?? {}).filter((key) => configs[key]?.isOpen);
  const baseZIndex = 1300;

  return (
    <Box>
      {openModals.map((modalKey, index) => {
        const { component: Component, isOpen, componentProps, modalProps } = configs[modalKey];
        const { onClose } = componentProps || { onClose: () => {} };
        const handleModalClose = () => {
          const proceed = () => {
            if (onClose) {
              onClose();
            }
            hideModalAction(modalKey);
          };
          const closeInterceptor = getCloseInterceptor(modalKey);
          if (closeInterceptor) {
            closeInterceptor(proceed);
          } else {
            proceed();
          }
        };

        const zIndex = baseZIndex + index * 2;

        return (
          <Modal
            open={isOpen}
            key={modalKey}
            onClose={handleModalClose}
            {...modalProps}
            sx={{ zIndex, ...modalProps?.sx }}
          >
            <Suspense>
              <Component {...componentProps} modalKey={modalKey} onClose={handleModalClose} />
            </Suspense>
          </Modal>
        );
      })}
    </Box>
  );
};
